import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaletteStore } from '@repletoryreach/sdk-utils/store';
import { Kbd, Text } from '@repletoryreach/sdk-ui';
import { ADMIN_NAV } from '../../lib/nav';
import { useT } from '../../i18n/I18nProvider';

/**
 * Button + command-palette registry. Renders a fake "search" input that opens
 * the global Spotlight when clicked; also registers every admin nav target as
 * a navigable command so users can jump from the keyboard.
 */
export function SpotlightLauncher() {
  const t = useT();
  const navigate = useNavigate();
  const openPalette = usePaletteStore((s) => s.openPalette);

  // Register every nav target as a palette command. Cleanup unregister on
  // unmount in case the component remounts (e.g. HMR).
  useEffect(() => {
    const register = usePaletteStore.getState().register;
    const cleanups: Array<() => void> = [];
    for (const section of ADMIN_NAV) {
      const sectionLabel = t(section.labelKey);
      for (const item of section.items) {
        const itemLabel = t(item.labelKey);
        const off = register({
          id: `nav:${item.path}`,
          title: t('shell.palette.goTo', { label: itemLabel }),
          group: sectionLabel,
          keywords: [itemLabel.toLowerCase(), sectionLabel.toLowerCase()],
          run: () => navigate(item.path)
        });
        cleanups.push(off);
      }
    }
    return () => cleanups.forEach((fn) => fn());
  }, [navigate, t]);

  return (
    <button
      type="button"
      onClick={() => openPalette()}
      aria-label={t('shell.palette.open')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        height: 32,
        minWidth: 320,
        padding: '0 6px 0 10px',
        background: 'color-mix(in srgb, var(--rr-color-surface) 80%, transparent)',
        border: '1px solid var(--rr-color-border)',
        borderRadius: 10,
        color: 'var(--rr-color-text-muted)',
        cursor: 'pointer'
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
          <circle cx="6" cy="6" r="3.5" />
          <path d="M9 9l3.5 3.5" />
        </svg>
        <Text fz="xs">{t('shell.palette.searchPlaceholder')}</Text>
      </span>
      <span style={{ display: 'inline-flex', gap: 2 }}>
        <Kbd>⌘</Kbd>
        <Kbd>K</Kbd>
      </span>
    </button>
  );
}
